// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TySupportTicket } from "@/types";
import InputGroups from "@/UI/components/InputGroups";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type FormSupportTicketPopupDetails = {
    onSuccess?: () => void;
};

type Props = {
    onClose: () => void;
    details?: FormSupportTicketPopupDetails;
};

// --- Constants ----------------------------------------------------------------------------------

/** Mirrors the maxlength on the backend model — keep both in sync. */
const TITLE_MAX_LENGTH = 80;

// --- Logic --------------------------------------------------------------------------------------

export default function FormSupportTicketPopup({ onClose, details }: Props) {
    const t = useTranslation();

    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");

    const {
        data: submitData,
        loading: submitting,
        error,
        invoke,
    } = useFetch<TyApiResponse<{ ticket: TySupportTicket }>>("/support/", { method: "POST" }, false);

    useEffect(() => {
        if (!submitData?.success) return;
        details?.onSuccess?.();
        onClose();
    }, [submitData]);

    const isValid = title.trim().length > 0 && message.trim().length > 0;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid || submitting) return;
        invoke({ data: { title: title.trim(), message: message.trim() } });
    };

    return (
        <div className="form-support-popup">
            {/* --- Header --------------------------------------------------------------------- */}
            <div className="form-support-popup-header">
                <p className="form-support-popup-title">{t.formSupport.createTitle}</p>
                <button type="button" className="form-support-popup-close" onClick={onClose} aria-label="Close">
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            {/* --- Body ----------------------------------------------------------------------- */}
            <form className="form-support-popup-body" onSubmit={handleSubmit} noValidate>
                <div className="fsp-fields">
                    <InputGroups icon="/icons/title-icon.svg" title={t.formSupport.titleSection}>
                        <div className="fsp-field">
                            <input
                                className="fsp-input"
                                type="text"
                                maxLength={TITLE_MAX_LENGTH}
                                placeholder={t.formSupport.titlePlaceholder}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                disabled={submitting}
                            />
                            <span className="fsp-counter">
                                {title.length} / {TITLE_MAX_LENGTH}
                            </span>
                        </div>
                    </InputGroups>

                    <InputGroups icon="/icons/inputs-group-icon.svg" title={t.formSupport.messageSection}>
                        <textarea
                            className="fsp-textarea"
                            rows={7}
                            placeholder={t.formSupport.messagePlaceholder}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={submitting}
                        />
                    </InputGroups>

                    <div className="fsp-note">
                        <img src="/icons/info-circle-icon.svg" alt="" width={16} height={16} />
                        <span>{t.formSupport.contactNote}</span>
                    </div>

                    {error && <p className="fsp-error">{error}</p>}
                </div>

                {/* --- Footer ------------------------------------------------------------------ */}
                <div className="form-support-popup-footer">
                    <button type="button" className="fsp-btn fsp-btn--cancel" onClick={onClose} disabled={submitting}>
                        {t.formSupport.cancel}
                    </button>
                    <button type="submit" className="fsp-btn fsp-btn--submit" disabled={submitting || !isValid}>
                        {submitting ? <span className="fsp-spinner" /> : t.formSupport.submit}
                    </button>
                </div>
            </form>
        </div>
    );
}
